import { ATeacher } from 'kuaidong-api'
import { BaseLoginService } from 'kuaidong-service'
import { GlobalConfigRouteMetaEnum } from 'kuaidong-model'
import { Route, NavigationGuardNext, RawLocation } from 'vue-router'
import { loginStore, teacherStore, globalConfigStore, menuRoleStore, systemStore } from 'kuaidong-store'
import { AppUtil, BrowserUtil, RouterUtil, EventEmitter, VueUtil, BaseLogger } from 'kuaidong-lib'
import router, { ignoreLoginRoutes, ignoreAuthRoutes, ignoreLoginInBrowserRoutes, authRouter } from '../router'

export enum LoginEvent {
  Authed = 'authed',
  Logined = 'logined',
}

const eventEmitter = new EventEmitter()

export default class LoginService extends BaseLoginService {
  public static once(type: string, handler: Function) {
    eventEmitter.once(type, handler)
  }

  public static async initBeforeLogin(route?: Route) {
    if (route && route.meta) {
      if (route.meta[GlobalConfigRouteMetaEnum.All]) {
        await globalConfigStore.ensureConfigIsLoaded()
      }

      if (route.meta[GlobalConfigRouteMetaEnum.Drills]) {
        await globalConfigStore.ensureDrillsIsLoaded()
      }

      if (route.meta[GlobalConfigRouteMetaEnum.Homework]) {
        await globalConfigStore.ensureHomeworkIsLoaded()
      }

      if (route.meta[GlobalConfigRouteMetaEnum.DrillCategories]) {
        await globalConfigStore.ensureDrillCategoriesIsLoaded()
      }
    }
  }

  public static async initAfterAuthed() {
    if (!teacherStore.teacher) {
      await teacherStore.loadTeacher()
    }

    if (!teacherStore.userRoleInfo) {
      await teacherStore.loadUserRoleInfo()
    }

    if (!menuRoleStore.menuRole) {
      await menuRoleStore.ensureMenuRoleConfigIsLoaded()
    }
  }

  public static async route(target: string) {
    location.replace(target)
  }

  public static async isApplied() {
    const authRecords = await ATeacher.allAuthRecords()
    return authRecords && authRecords.length > 0
  }

  public static async routeToAuth(_route: Route, _next?: NavigationGuardNext<Vue>) {
    const route = (await this.isApplied()) ? authRouter.authInprogressRoute : authRouter.authRoute

    if (!_route || _route.name !== route.name) {
      _next
        ? _next({ ...route, query: { channel: _route.query.qd }, replace: true })
        : router.replace({ name: route.name, query: { channel: _route.query.qd } })
    } else {
      _next && _next(false)
    }
  }

  public static async checkWXWebOauthTargetAndRedirect(href = location.href) {
    if (href && this.hasWXWebOauthTargetHash(href) && !RouterUtil.isCurRoute(authRouter.accountLoginRoute)) {
      // 微信登录后刷新登录状态
      await loginStore.loadUserTeacherId()

      location.replace(this.decodeWXWebOauthTargetHash(href))

      return true
    }

    return false
  }

  public static async login(_route?: Route, _replace = true, _next?: NavigationGuardNext<Vue>) {
    if (!(await AppUtil.isUnderApp())) {
      // 当前页已经是登录页不做跳转
      if (!_route && RouterUtil.isCurRoute(authRouter.accountLoginRoute)) {
        return false
      }

      let queryStr: string

      if (_route && _route.fullPath) {
        queryStr = `${location.origin}${location.pathname}#${_route.fullPath}`
      } else {
        queryStr = location.href
      }

      // if (BrowserUtil.isWechat() && !BrowserUtil.isWxWork() && !BrowserUtil.isWeapp()) {
      //   _next && _next(false)

      //   // 微信重定向到target时会将hash部分抹去，所以需要处理下target。
      //   // https://www.cnblogs.com/fan-zha/p/11250549.html
      //   queryStr = this.encodeWXWebOauthTargetHash(queryStr)

      //   // 微信的登录地址, 微信登录之后固定跳转到当前页面
      //   if (_replace) {
      //     location.replace(`${process.env.VUE_APP_CRAZYROPE_API}/wx_web_oauth_entry?target=${queryStr}`)
      //   } else {
      //     location.href = `${process.env.VUE_APP_CRAZYROPE_API}/wx_web_oauth_entry?target=${queryStr}`
      //   }
      // } else {
      // 普通网页的登录地址
      if (_next) {
        _next({
          ...authRouter.accountLoginRoute,
          query: { target: queryStr },
          replace: _replace,
        })
      } else {
        if (_replace) {
          router.replace({ name: authRouter.accountLoginRoute.name, query: { target: queryStr } })
        } else {
          router.push({ name: authRouter.accountLoginRoute.name, query: { target: queryStr } })
        }
      }
      // }

      return true
    }

    return false
  }

  public static async needAuth(_route: Route) {
    if (RouterUtil.isForceAuth(_route)) {
      return true
    }

    if (_route.name && (_route.name.startsWith('auth') || _route.name.startsWith('ad_auth'))) {
      return false
    }

    if (ignoreAuthRoutes.some((route) => _route.name === route.name)) {
      return false
    }

    if (_route.meta?.ignoreAuth) {
      return false
    }

    return true
  }

  public static async needLogin(_route: Route) {
    if (RouterUtil.isForceAuth(_route)) {
      return true
    }

    if (_route.name === authRouter.accountLoginRoute.name) {
      return false
    }

    if (ignoreLoginRoutes.some((route) => _route.name === route.name)) {
      return false
    }

    if (_route.meta?.ignoreLogin) {
      return false
    }

    if (!BrowserUtil.isWechat() && !(await AppUtil.isUnderApp())) {
      if (ignoreLoginInBrowserRoutes.some((route) => _route.name === route.name)) {
        return false
      }
    }

    return true
  }

  public static async isLogined() {
    if (BrowserUtil.isWeapp()) {
      return !!AppUtil.getRequestToken()
    }

    return loginStore.isLogined
  }

  /**
   * 处理用户反馈的弹框逻辑
   *
   * @param _to 目标路由对象
   * @param _from 来源路由对象
   * @returns 无返回值
   */
  public static async processFeedBack(_to: Route, _from: Route) {
    const pathArr = [
      '/normal-activity-stat', // 新学期作业
      '/homework-plan-teacher-list', // 布置作业
      '/homework-list', // 检查作业
      '/competition-list', // 体育比赛
      '/famous-teacher-list', // 名师讲堂
      '/lesson-plan-list', // 教案资源
      '/pe-points-system', // 体育积分
      '/run-task-list', // 运动任务
      '/ai-assistant', // ai助手
      '/one-hour-self-apply-list', // 运动申报
      '/exam-list', //体质测试
      '/rpe-test-list', // RPE测试
      '/winter-2023-physical-health-knowledge-list', //体育知识
      '/online-course-list', //在线体育课
      '/gymnastics-list-page', // 广播体操
      '/long-break-list', //特色大课间
      '/winter-2023-special-training-list', // 专项技能
      '/ep-intro', //运动处方
      '/approval-leave-list', // 请假审批
      '/pe-class/pe-class-evaluation', // 体育课评价
      '/pe-class-big-recess/pe-class-big-recess-attendance', // 大课间评价
      '/one-hour-rank', // 校外一小时
    ]
    // 功能区页面返回首页或者更多应用时，记录上一个页面路径，弹出用户反馈弹框
    if (_to.name === 'home' && pathArr.includes(_from.path as string)) {
      systemStore.setFeedBackPath(_from.path)
    }

    if (_to.name === 'more_app' && pathArr.includes(_from.path as string)) {
      systemStore.setFeedBackPath(_from.path)
    }
  }

  public static async routerBeforeEachHook(_to: Route, _from: Route, _next: NavigationGuardNext<Vue>) {
    if (_to.query[this.RequestTokenQueryKey]) {
      AppUtil.setRequestToken(_to.query[this.RequestTokenQueryKey] as string)
    }
    await this.processFeedBack(_to, _from)

    // Check if it is the first visit.
    if (!_from.name && _from.fullPath === '/') {
      if (await LoginService.checkWXWebOauthTargetAndRedirect()) {
        return _next(false)
      }
    }

    this.initBeforeLogin(_to)

    if (!(await LoginService.needLogin(_to))) {
      return _next()
    }

    // Check if it is logined/authed already.
    if (loginStore.userTeacherId === null) {
      await loginStore.loadUserTeacherId()

      if (loginStore.isLogined) {
        BaseLogger.setUserId(loginStore.userTeacherId!.user_id!)
        VueUtil.setARMSConfig({ uid: loginStore.userTeacherId!.user_id })
      }
    }

    if (!(await this.isLogined())) {
      if (RouterUtil.isForceAuth(_to)) {
        return LoginService.login(_to, true, _next)
      }

      return LoginService.login(_to, false, _next)
    }

    eventEmitter.fire(LoginEvent.Logined)

    if (!(await LoginService.needAuth(_to))) {
      return _next()
    }

    if (loginStore.isAuthed) {
      await LoginService.initAfterAuthed()

      if (RouterUtil.isForceAuth(_to)) {
        return _next({ ...RouterUtil.removeForceAuthFlagFromRoute(_to), replace: true } as RawLocation)
      }

      return _next()
    } else {
      return LoginService.routeToAuth(_to, _next)
    }
  }
}
